/**
 * useDeleteAppJob Hook
 *
 * Custom React Query mutation hook for deleting appJobs.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appJobService } from '../../../api/app-job.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseDeleteAppJobOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deleting a appJob
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useDeleteAppJob = (
  tenantId: string,
  options?: UseDeleteAppJobOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appJobId: string) =>
      appJobService.deleteAppJob(tenantId, appJobId),
    onSuccess: () => {
      // Invalidate all appJob queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['appJobs', tenantId],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
